import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { autoUpdate, flip, FloatingFocusManager, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Notification01Icon } from '@hugeicons/core-free-icons';
import type { FeedItem } from '@overlayqa/shared';
import { reviewCompletionDestination } from '@overlayqa/shared';
import { NotificationInbox } from '@overlayqa/shared/ui';
import '@overlayqa/shared/ui/styles.css';
import { useInboxBadge } from '../hooks/notifications/useInboxBadge';
import { useNotifications } from '../hooks/useNotifications';

export function NotificationBell() {
  const { notifications, announcements, accountKey, mutateInbox, loading, loadError, reload } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open, onOpenChange: setOpen, placement: 'bottom-end', whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });
  const click = useClick(context), dismiss = useDismiss(context), role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const items: FeedItem[] = [
    ...notifications.map(item => ({ source: 'notification' as const, item })),
    ...announcements.map(item => ({ source: 'announcement' as const, item })),
  ];
  const badge = useInboxBadge(accountKey, items, open);
  const totalNew = badge.total, newAlertCount = badge.alerts, hasNewAnnouncement = badge.announcements > 0;
  function openItem(feed: FeedItem) {
    if (feed.source === 'announcement') {
      if (feed.item.ctaUrl && /^https?:\/\//i.test(feed.item.ctaUrl)) window.open(feed.item.ctaUrl, '_blank', 'noopener,noreferrer');
    } else {
      const n = feed.item;
      const savedPage = reviewCompletionDestination(n);
      if (savedPage) window.open(savedPage, '_blank', 'noopener,noreferrer');
      else navigate(n.type === 'client_review_done' && n.data.projectId ? `/projects/${n.data.projectId}/issues` : `/issues/${n.entityId}`);
    }
    setOpen(false);
  }
  // Same badge priority as the extension toolbar: alerts, news, activity.
  const badgeColor = newAlertCount > 0 ? 'var(--color-primary)' : hasNewAnnouncement ? 'var(--oq-error-light)' : 'var(--oq-success)';
  return <>
    <button type="button" data-inbox-loading={loading} ref={refs.setReference} className="relative p-3 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
      {...getReferenceProps({ 'aria-label': `Notifications${totalNew ? ` (${totalNew} new)` : ''}`, title: 'Notifications' })}>
      <HugeiconsIcon icon={Notification01Icon} size={20} strokeWidth={2} color="currentColor" aria-hidden="true" />
      {totalNew > 0 && (newAlertCount > 0
        ? <span data-notification-badge className="absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white" style={{ backgroundColor: badgeColor }}>{newAlertCount > 99 ? '99+' : newAlertCount}</span>
        : <span data-notification-badge className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full" style={{ backgroundColor: badgeColor }} />)}
    </button>
    {open && <FloatingPortal><FloatingFocusManager context={context} modal={false}>
      <div ref={refs.setFloating} style={floatingStyles} className="w-80 max-w-[calc(100vw-1rem)] rounded-lg border border-gray-200 bg-white shadow-lg z-50 overflow-hidden"
        {...getFloatingProps({ 'aria-label': 'Notifications' })}>
        <NotificationInbox key={accountKey} items={items} onAction={mutateInbox} onOpen={openItem} onClose={() => setOpen(false)}
          loading={loading} loadError={loadError} onReload={() => void reload()} />
      </div>
    </FloatingFocusManager></FloatingPortal>}
  </>;
}
